import { createContext, FC, PropsWithChildren, useContext, useRef } from "react";
import useDataInitializer from "../hooks/useDataInitializer";
import useIdentity from "../hooks/useIdentity";
import { useMeApi } from "../http/me/api";
import { LanguageCode } from "../types";

type Operations = {
  read: boolean;
  insert: boolean;
  update: boolean;
  delete: boolean;
};

export type UserSettingsContextType = {
  me: {
    id: string;
    distanceInMiles: boolean;
    preferredLanguage: LanguageCode | null;
    userSubsidiariesIDs: string[];
    firstName: string;
    lastName: string;
    birthDate: string;
    currentCostCenter?: string;
    email?: string;
    department?: string;
    drivingLicenseExpiration: string;
  };
  notifications?: {
    usersMissingCostCenterCount?: number;
    dueDateExpiringSoon?: number;
    dueDateExpired?: number;
    taskToDo?: number;
    carPurchaseArrivingSoon?: number;
    carPurchaseExpired?: number;
    carLeasingExpiringSoon?: number;
    carLeasingExpired?: number;
  };
  isLoading: boolean;
  isManager: boolean;
  canSeeCars: boolean;
  canSeeSubsidiaries: boolean;
  // canSeeOthers: boolean;
  // canSeeAdmin: boolean;
  canSeeAdministration: boolean;
  permissions: {
    carDrivers: Operations;
    carLeasings: Operations;
    carPlates: Operations;
    carPurchases: Operations;
    claims: Operations;
    claimTypes: Operations;
    fines: Operations;
    carTasks: Operations;
    cars: Operations;
    clients: Operations;
    costTypeCategories: Operations;
    costTypes: Operations;
    costs: Operations;
    costsImports: Operations;
    defaultTask: Operations;
    dueDateTypes: Operations;
    dueDates: Operations;
    highwayPasses: Operations;
    leasingCompanies: Operations;
    maintenances: Operations;
    maintenanceTypes: Operations;
    manufacturers: Operations;
    mileages: Operations;
    otherCarAssignment: Operations;
    roles: Operations;
    subsidiaries: Operations;
    subsidiaryVatCodes: Operations;
    suppliers: Operations;
    userCostCenters: Omit<Operations, "insert" | "delete">;
    userRoles: Omit<Operations, "insert" | "delete">;
    users: Operations;
    carPlannings: Operations;
    rentalCars: Operations;
  };
  error: unknown;
};

const UserSettingsContext = createContext<UserSettingsContextType>(undefined!);

type UserSettingsProviderProps = PropsWithChildren<{}>;

const UserSettingsProvider: FC<UserSettingsProviderProps> = ({ children }) => {
  const identity = useIdentity();
  const aborter = useRef(new AbortController());
  const api = useMeApi();

  const result = api.useSettings(aborter.current.signal, identity?.email);
  const [data, isInitialized] = useDataInitializer(result?.data, result?.error);

  const has = (permissionName: string) => {
    // return permissionName === "cars:read";
    return !!data?.permissions?.some((p) => p === permissionName);
  };

  const contextValue: UserSettingsContextType = {
    me: {
      id: data?.me.id ?? "",
      distanceInMiles: !!data?.me.distanceInMiles,
      firstName: data?.me.firstName ?? "",
      lastName: data?.me.lastName ?? "",
      birthDate: data?.me.birthDate ?? "",
      drivingLicenseExpiration: data?.me.drivingLicenseExpiration ?? "",
      userSubsidiariesIDs: data?.me.userSubsidiariesIDs ?? [],
      preferredLanguage: data?.me.preferredLanguage ?? null,
    },
    notifications: data?.notifications,
    isLoading: !isInitialized,
    isManager: !!data?.isManager,
    canSeeCars:
      has("cars:read") ||
      has("car-drivers:read") ||
      has("car-tasks:read") ||
      has("claims:read") ||
      has("fines:read") ||
      has("due-dates:read") ||
      has("highway-passes:read") ||
      has("leasing-companies:read") ||
      has("maintenances:read") ||
      has("mileages:read") ||
      has("other-car-assignment:read") ||
      has("suppliers:read"),
    canSeeSubsidiaries: has("subsidiaries:read") || has("subsidiary-vat-codes:read"),
    // canSeeOthers: has("car-leasings:read") || has("car-plates:read") || has("costs:read"),
    // canSeeAdmin: has("roles:read") || has("clients:read"),
    canSeeAdministration: !!data?.permissions.length,
    // canSeeAdministration:
    //   has("cars:read") ||
    //   has("car-drivers:read") ||
    //   has("car-tasks:read") ||
    //   has("due-dates:read") ||
    //   has("highway-passes:read") ||
    //   has("leasing-companies:read") ||
    //   has("mileages:read") ||
    //   has("suppliers:read") ||
    //   has("car-purchases:read") ||
    //   has("costs-imports:read") ||
    //   has("subsidiaries:read") ||
    //   has("subsidiary-vat-codes:read") ||
    //   has("manufacturers:read") ||
    //   has("other-car-assignment:read") ||
    //   has("users:read") ||
    //   has("car-leasings:read") ||
    //   has("car-plates:read") ||
    //   has("costs:read"),
    permissions: {
      carDrivers: {
        read: has("car-drivers:read"),
        insert: has("car-drivers:insert"),
        update: has("car-drivers:update"),
        delete: has("car-drivers:delete"),
      },
      carLeasings: {
        read: has("car-leasings:read"),
        insert: has("car-leasings:insert"),
        update: has("car-leasings:update"),
        delete: has("car-leasings:delete"),
      },
      carPlates: {
        read: has("car-plates:read"),
        insert: has("car-plates:insert"),
        update: has("car-plates:update"),
        delete: has("car-plates:delete"),
      },
      carPlannings: {
        read: has("car-plannings:read"),
        insert: has("car-plannings:insert"),
        update: has("car-plannings:update"),
        delete: has("car-plannings:delete"),
      },
      carPurchases: {
        read: has("car-purchases:read"),
        insert: has("car-purchases:insert"),
        update: has("car-purchases:update"),
        delete: has("car-purchases:delete"),
      },
      claims: {
        read: has("claims:read"),
        insert: has("claims:insert"),
        update: has("claims:update"),
        delete: has("claims:delete"),
      },
      fines: {
        read: has("fines:read"),
        insert: has("fines:insert"),
        update: has("fines:update"),
        delete: has("fines:delete"),
      },
      claimTypes: {
        read: has("claim-types:read"),
        insert: has("claim-types:insert"),
        update: has("claim-types:update"),
        delete: has("claim-types:delete"),
      },
      carTasks: {
        read: has("car-tasks:read"),
        insert: has("car-tasks:insert"),
        update: has("car-tasks:update"),
        delete: has("car-tasks:delete"),
      },
      cars: {
        read: has("cars:read"),
        insert: has("cars:insert"),
        update: has("cars:update"),
        delete: has("cars:delete"),
      },
      clients: {
        read: has("clients:read"),
        insert: has("clients:insert"),
        update: has("clients:update"),
        delete: has("clients:delete"),
      },
      costTypeCategories: {
        read: has("cost-type-categories:read"),
        insert: has("cost-type-categories:insert"),
        update: has("cost-type-categories:update"),
        delete: has("cost-type-categories:delete"),
      },
      costTypes: {
        read: has("cost-types:read"),
        insert: has("cost-types:insert"),
        update: has("cost-types:update"),
        delete: has("cost-types:delete"),
      },
      costs: {
        read: has("costs:read"),
        insert: has("costs:insert"),
        update: has("costs:update"),
        delete: has("costs:delete"),
      },
      costsImports: {
        read: has("costs-imports:read"),
        insert: has("costs-imports:insert"),
        update: has("costs-imports:update"),
        delete: has("costs-imports:delete"),
      },
      defaultTask: {
        read: has("default-tasks:read"),
        insert: has("default-tasks:insert"),
        update: has("default-tasks:update"),
        delete: has("default-tasks:delete"),
      },
      dueDateTypes: {
        read: has("due-date-types:read"),
        insert: has("due-date-types:insert"),
        update: has("due-date-types:update"),
        delete: has("due-date-types:delete"),
      },
      dueDates: {
        read: has("due-dates:read"),
        insert: has("due-dates:insert"),
        update: has("due-dates:update"),
        delete: has("due-dates:delete"),
      },
      highwayPasses: {
        read: has("highway-passes:read"),
        insert: has("highway-passes:insert"),
        update: has("highway-passes:update"),
        delete: has("highway-passes:delete"),
      },
      leasingCompanies: {
        read: has("leasing-companies:read"),
        insert: has("leasing-companies:insert"),
        update: has("leasing-companies:update"),
        delete: has("leasing-companies:delete"),
      },
      maintenances: {
        read: has("maintenances:read"),
        insert: has("maintenances:insert"),
        update: has("maintenances:update"),
        delete: has("maintenances:delete"),
      },
      maintenanceTypes: {
        read: has("maintenance-types:read"),
        insert: has("maintenance-types:insert"),
        update: has("maintenance-types:update"),
        delete: has("maintenance-types:delete"),
      },
      manufacturers: {
        read: has("manufacturers:read"),
        insert: has("manufacturers:insert"),
        update: has("manufacturers:update"),
        delete: has("manufacturers:delete"),
      },
      mileages: {
        read: has("mileages:read"),
        insert: has("mileages:insert"),
        update: has("mileages:update"),
        delete: has("mileages:delete"),
      },
      otherCarAssignment: {
        read: has("other-car-assignment:read"),
        insert: has("other-car-assignment:insert"),
        update: has("other-car-assignment:update"),
        delete: has("other-car-assignment:delete"),
      },
      roles: {
        read: has("roles:read"),
        insert: has("roles:insert"),
        update: has("roles:update"),
        delete: has("roles:delete"),
      },
      subsidiaries: {
        read: has("subsidiaries:read"),
        insert: has("subsidiaries:insert"),
        update: has("subsidiaries:update"),
        delete: has("subsidiaries:delete"),
      },
      subsidiaryVatCodes: {
        read: has("subsidiary-vat-codes:read"),
        insert: has("subsidiary-vat-codes:insert"),
        update: has("subsidiary-vat-codes:update"),
        delete: has("subsidiary-vat-codes:delete"),
      },
      suppliers: {
        read: has("suppliers:read"),
        insert: has("suppliers:insert"),
        update: has("suppliers:update"),
        delete: has("suppliers:delete"),
      },
      userCostCenters: {
        read: has("user-cost-centers:read"),
        update: has("user-cost-centers:update"),
      },
      userRoles: {
        read: has("user-roles:read"),
        update: has("user-roles:update"),
      },
      users: {
        read: has("users:read"),
        insert: has("users:insert"),
        update: has("users:update"),
        delete: has("users:delete"),
      },
      rentalCars: {
        read: has("rental-cars:read"),
        insert: has("rental-cars:insert"),
        update: has("rental-cars:update"),
        delete: has("rental-cars:delete"),
      },
    },
    error: result.error,
  };

  return <UserSettingsContext.Provider value={contextValue}>{children}</UserSettingsContext.Provider>;
};

export const useUserSettings = () => {
  const context = useContext(UserSettingsContext);
  if (!context) {
    throw new Error("useUserSettings must be used within a UserSettingsProvider");
  }
  return context;
};

export default UserSettingsProvider;
